import type { JSX } from 'react';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { GroupView, SchedDependency } from '../../api';
import {
  TaskListHeader,
  TaskListRows,
  type ListHandlers,
  type PredecessorLink,
} from './TaskList';
import { Timeline, type DragEdit } from './Timeline';
import type { OutlineRow } from './outline';
import type { BaselineDisplay } from './baselineView';
import {
  type ColumnKey,
  type ColumnSpec,
  type ColumnWidths,
  type Orientation,
  loadSplit,
  loadWidths,
  saveSplit,
  saveWidths,
  stretchToFit,
  totalWidth,
} from './columns';

/**
 * List and chart together, in one of two arrangements.
 *
 * Both use the same mechanism: two scrolling panes with a draggable divider
 * between them, and their vertical scroll mirrored so row three in the list
 * always sits beside bar three in the chart. One mechanism rather than two
 * means the divider, the scroll coupling and the full-width behaviour are
 * written once and work the same way in both arrangements.
 *
 * The list stretches to whatever width it is given, with the spare width going
 * to the name column; the chart extends its date range so its grid reaches the
 * right edge instead of stopping where the bars happen to end.
 */
const MIN_PANE = 140;

export function ScheduleBoard({
  rows,
  groups,
  dependencies,
  collapsed,
  onToggleCollapse,
  handlers,
  orientation,
  onDragPreview,
  onDragCommit,
  onDragCancel,
  baseline,
  columns,
  predecessors,
}: {
  rows: OutlineRow[];
  groups: GroupView[];
  dependencies: SchedDependency[];
  collapsed: ReadonlySet<string>;
  onToggleCollapse: (groupId: string) => void;
  handlers: ListHandlers;
  orientation: Orientation;
  onDragPreview?: (taskId: string, edit: DragEdit) => void;
  onDragCommit?: (taskId: string, edit: DragEdit) => void;
  onDragCancel?: () => void;
  baseline: BaselineDisplay | null;
  /** The columns to show - fewer on a phone, no variance without a baseline. */
  columns: readonly ColumnSpec[];
  predecessors: ReadonlyMap<string, readonly PredecessorLink[]>;
}): JSX.Element {
  const [widths, setWidths] = useState<ColumnWidths>(loadWidths);
  const [split, setSplit] = useState<number>(() => loadSplit(orientation, 520));

  const outer = useRef<HTMLDivElement>(null);
  const listPane = useRef<HTMLDivElement>(null);
  const chartPane = useRef<HTMLDivElement>(null);
  const syncing = useRef(false);

  const [listWidth, setListWidth] = useState(0);
  const [chartWidth, setChartWidth] = useState(0);

  const vertical = orientation === 'vertical';

  useEffect(() => {
    setSplit(loadSplit(orientation, vertical ? 520 : 300));
  }, [orientation, vertical]);

  // Panes report their own width, so the list can fill it and the chart can
  // draw its grid all the way to the edge.
  useLayoutEffect(() => {
    const observed: [HTMLDivElement | null, (value: number) => void][] = [
      [listPane.current, setListWidth],
      [chartPane.current, setChartWidth],
    ];
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        for (const [element, set] of observed) {
          if (element === entry.target) set(entry.contentRect.width);
        }
      }
    });
    for (const [element] of observed) if (element) observer.observe(element);
    return () => observer.disconnect();
  }, [orientation]);

  // Mirrors the vertical scroll between the two panes, guarded against the
  // echo a programmatic scroll would otherwise cause.
  useEffect(() => {
    const list = listPane.current;
    const graph = chartPane.current;
    if (!list || !graph) return;

    const mirror = (from: HTMLDivElement, to: HTMLDivElement) => () => {
      if (syncing.current) return;
      syncing.current = true;
      to.scrollTop = from.scrollTop;
      requestAnimationFrame(() => {
        syncing.current = false;
      });
    };
    const fromList = mirror(list, graph);
    const fromChart = mirror(graph, list);
    list.addEventListener('scroll', fromList);
    graph.addEventListener('scroll', fromChart);
    return () => {
      list.removeEventListener('scroll', fromList);
      graph.removeEventListener('scroll', fromChart);
    };
  }, [orientation]);

  const resizeColumn = (key: ColumnKey, width: number): void => {
    setWidths((previous) => {
      const next = { ...previous, [key]: Math.round(width) };
      saveWidths(next);
      return next;
    });
  };

  const startDividerDrag = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      const box = outer.current?.getBoundingClientRect();
      if (!box) return;

      const move = (moveEvent: MouseEvent): void => {
        const raw = vertical
          ? moveEvent.clientX - box.left
          : moveEvent.clientY - box.top;
        const limit = (vertical ? box.width : box.height) - MIN_PANE;
        setSplit(Math.round(Math.min(Math.max(MIN_PANE, raw), Math.max(MIN_PANE, limit))));
      };
      const stop = (): void => {
        window.removeEventListener('mousemove', move);
        window.removeEventListener('mouseup', stop);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        setSplit((value) => {
          saveSplit(orientation, value);
          return value;
        });
      };

      document.body.style.cursor = vertical ? 'col-resize' : 'row-resize';
      document.body.style.userSelect = 'none';
      window.addEventListener('mousemove', move);
      window.addEventListener('mouseup', stop);
    },
    [orientation, vertical],
  );

  // In the stacked arrangement the list gets the whole window width, so the
  // columns stretch. Side by side it gets the pane, which may be narrower than
  // the columns need - then it scrolls sideways on its own.
  const effective = stretchToFit(widths, listWidth, columns);
  const columnsWidth = totalWidth(widths, columns);

  return (
    <div
      ref={outer}
      className="pst-tabular"
      style={{
        display: 'flex',
        flexDirection: vertical ? 'row' : 'column',
        flex: 1,
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <div
        ref={listPane}
        style={{
          [vertical ? 'width' : 'height']: split,
          flex: '0 0 auto',
          minWidth: 0,
          minHeight: 0,
          overflowX: 'auto',
          overflowY: 'scroll',
          // The chart carries the visible scrollbar; this one is only a slave.
          scrollbarWidth: 'none',
          background: 'var(--mantine-color-body)',
        }}
      >
        <div style={{ minWidth: columnsWidth }}>
          <TaskListHeader columns={columns} widths={effective} onResize={resizeColumn} />
          <TaskListRows
            rows={rows}
            groups={groups}
            columns={columns}
            widths={effective}
            collapsed={collapsed}
            onToggleCollapse={onToggleCollapse}
            handlers={handlers}
            baseline={baseline}
            predecessors={predecessors}
          />
        </div>
      </div>

      <div
        role="separator"
        aria-orientation={vertical ? 'vertical' : 'horizontal'}
        title="Teilung verschieben"
        onMouseDown={startDividerDrag}
        style={{
          flex: '0 0 auto',
          [vertical ? 'width' : 'height']: 6,
          cursor: vertical ? 'col-resize' : 'row-resize',
          background: 'var(--mantine-color-default-border)',
          backgroundClip: 'content-box',
          [vertical ? 'borderLeft' : 'borderTop']: '2px solid transparent',
          [vertical ? 'borderRight' : 'borderBottom']: '2px solid transparent',
        }}
      />

      <div
        ref={chartPane}
        style={{ flex: 1, minWidth: 0, minHeight: 0, overflow: 'auto' }}
      >
        <Timeline
          rows={rows}
          groups={groups}
          dependencies={dependencies}
          minWidth={chartWidth}
          showLabels={!vertical}
          onDragPreview={onDragPreview}
          onDragCommit={onDragCommit}
          onDragCancel={onDragCancel}
          baseline={baseline}
        />
      </div>
    </div>
  );
}
